import Warning from '@mui/icons-material/Warning';
import { Link, Typography, useTheme } from '@qbe/ads-core';
import React, { useEffect } from 'react';

import Modal from '~/components-ds/Modal';
import { QBE_HOME_LINK } from '~/constants/app';
import { CALL_CENTER_CONTACT_NUMBER } from '~/constants/domain-specific';
import { useAppContent } from '~/hooks';
import { qalTrackBusinessErrorAction, qalTrackTechnicalErrorAction } from '~/thunks/analytics';
import type { ErrorType } from '~/types/error';
import { PaymentErrorCode } from '~/types/payments';
import { formatSixDigitPhone } from '~/utils';

import { logError } from '../../utils/logError';

interface AppErrorModalProps {
  errors: ErrorType[];
}

/** @note any kind of error code can be added to this list not just payments */
const errorCodeToModalContent = {
  [String(PaymentErrorCode.MS1)]: (
    <Typography>
      Your payment was successful, but unfortunately we weren't able to create your policy. Please
      contact us on{' '}
      <Link
        data-track={`telL${CALL_CENTER_CONTACT_NUMBER}`}
        href={`tel:${CALL_CENTER_CONTACT_NUMBER}`}
      >
        {formatSixDigitPhone(CALL_CENTER_CONTACT_NUMBER)}
      </Link>{' '}
      so that we can resolve this for you.
    </Typography>
  ),
};

interface Content {
  title: string;
  defaultErrorContent: React.ReactElement | React.ReactElement[];
  primaryAction: string;
  primaryActionLink: string;
}

const defaultErrorContent = (
  <Typography>
    Something went wrong. Please contact us on{' '}
    <Link
      data-track={`tel:${CALL_CENTER_CONTACT_NUMBER}`}
      href={`tel:${CALL_CENTER_CONTACT_NUMBER}`}
    >
      {formatSixDigitPhone(CALL_CENTER_CONTACT_NUMBER)}
    </Link>{' '}
    to proceed
  </Typography>
);

const ErrorContent = ({
  errors,
  defaultError,
}: {
  errors: ErrorType[];
  defaultError: React.ReactElement | React.ReactElement[];
}) => {
  if (errors?.length > 0) {
    const { code } = errors[0];
    return (code && errorCodeToModalContent[code]) || defaultError;
  }
  return defaultError;
};

const AppErrorModal = ({ errors }: AppErrorModalProps) => {
  const {
    status: contentStatus,
    getTextContent,
    getJSXContent,
  } = useAppContent({ basePath: 'dsme.appErrorModal' });
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });

  const theme = useTheme();

  useEffect(() => {
    errors.forEach((error) => {
      if (error?.errorType === 'business') {
        qalTrackBusinessErrorAction(error);
      } else {
        logError(error);
        qalTrackTechnicalErrorAction(error);
      }
    });
  }, [errors]);

  const content: Content =
    contentStatus === 'ready'
      ? {
          title: getTextContent('title'),
          defaultErrorContent: getJSXContent('content.defaultError.description'),
          primaryAction: getSharedContent('returnToQbeButtonLabel'),
          primaryActionLink: getSharedContent('qbeHome'),
        }
      : {
          title: 'Oops!',
          defaultErrorContent,
          primaryAction: 'Return to QBE',
          primaryActionLink: QBE_HOME_LINK,
        };

  return (
    <Modal
      id="app-error-modal"
      data-testid="app-error-modal"
      open
      title={content.title}
      iconProps={{
        muiIcon: Warning,
        // TODO - https://qbe-appservices.atlassian.net/browse/AD1-445
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        color: theme.color.icon.error,
      }}
      primaryBtnProps={{
        label: content.primaryAction,
        href: content.primaryActionLink,
        'data-track': 'return to QBE',
      }}
      analyticsModalName="error-modal"
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
    >
      <ErrorContent errors={errors} defaultError={content.defaultErrorContent} />
    </Modal>
  );
};

export default AppErrorModal;
